<template>
  <div class="chatbot-proposal-modal" dir="rtl">
    <div v-if="is_loading" class="loading-wrap">
      <div class="loading"></div>
      <p>טוען פרטי הצעה...</p>
    </div>
    <div v-else>
      <div class="reasoning-box" v-if="proposal.ba_reasoning">
        <h4>הקושי שזוהה</h4>
        <p v-if="!parsedDifficultyReasons.length" class="raw-reasoning">{{ proposal.ba_reasoning }}</p>
        <div v-if="displayDifficultyReasons.length" class="reasoning-components">
          <section
            v-for="(item, index) in displayDifficultyReasons"
            :key="item.label + '-' + index"
            class="reasoning-component"
            :class="{ 'is-manual': item.manual }"
          >
            <div class="reasoning-component-header">
              <h5>{{ item.label }}</h5>
              <button
                type="button"
                class="refresh-icon-button"
                :class="{ 'is-spinning': is_category_refreshing && recalculatingLabel === item.label }"
                :disabled="is_submitting || is_category_refreshing"
                :title="`בדוק מחדש רק את ${item.label} (כל סטטוס) — לפי אופרציה`"
                :aria-label="`חשב מחדש עבור ${item.label}`"
                @click="refreshDifficultyComponent(item)"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <polyline points="1 20 1 14 7 14"></polyline>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                </svg>
              </button>
            </div>
            <div v-if="hasBreakdown(item.breakdown)" class="breakdown-grid">
              <div class="bd-row" v-if="item.breakdown.candidate_side">
                <span class="bd-label">מה יש למועמד/ת:</span>
                <span class="bd-value">{{ item.breakdown.candidate_side }}</span>
              </div>
              <div class="bd-row" v-if="item.breakdown.position_side">
                <span class="bd-label">מה דורשת המשרה:</span>
                <span class="bd-value">{{ item.breakdown.position_side }}</span>
              </div>
              <div class="bd-row bd-no" v-if="item.breakdown.why_no">
                <span class="bd-label">למה לא?</span>
                <span class="bd-value">{{ item.breakdown.why_no }}</span>
              </div>
              <div class="bd-row bd-yes" v-if="item.breakdown.why_yes">
                <span class="bd-label">למה כן?</span>
                <span class="bd-value">{{ item.breakdown.why_yes }}</span>
              </div>
            </div>
            <ul class="reasoning-list" v-else-if="item.reasons && item.reasons.length">
              <li v-for="(reason, reasonIndex) in item.reasons" :key="item.label + '-' + index + '-' + reasonIndex">
                {{ reason }}
              </li>
            </ul>
            <p v-else-if="item.manual" class="manual-hint">קטגוריה זו לא סומנה כקושי — הוסיפי כאן את ההתייחסות שלך.</p>

            <div v-if="item.label === 'שכר' && proposal.position_salary" class="salary-edit">
              <div class="salary-values">
                <span class="salary-values-label">שכר במשרה:</span>
                <span class="salary-values-text">{{ salaryDisplay }}</span>
              </div>
              <div class="salary-link" v-if="proposal.position">
                <link-entity :payload="proposal.position" :additional_data="{ resource_type: 'Position' }" />
              </div>
              <p class="salary-hint">עדכני את השכר במשרה ישירות — הנתון מתעדכן בלייב, בלי לרשום פעמיים.</p>
            </div>
            <div
              v-if="categoryVerdicts[item.label]"
              class="category-verdict"
              :class="'verdict-' + categoryVerdicts[item.label].decision"
            >
              <span class="verdict-badge">{{ verdictLabel(categoryVerdicts[item.label].decision) }}</span>
              <p v-if="categoryVerdicts[item.label].reasoning" class="verdict-reasoning">{{ categoryVerdicts[item.label].reasoning }}</p>
            </div>
            <!-- Coexisting learned rules for this dimension (no silent override) -->
            <div v-if="item.agent_type" class="coexist">
              <div v-if="disclosureLoading[item.label]" class="coexist-skeleton" aria-hidden="true">
                <div class="sk-line"></div>
                <div class="sk-line sk-short"></div>
              </div>
              <template v-else>
                <div v-if="disclosureError[item.label]" class="coexist-error">
                  <span>לא הצלחנו לטעון כללים קיימים.</span>
                  <button type="button" class="link-like" @click="fetchDisclosure(item)">נסי שוב</button>
                </div>
                <div v-else-if="hasCoexistingRules(item)" class="coexist-card">
                  <div class="coexist-head">כללים קיימים ({{ countFor(item) }}/{{ ceilingFor(item) }})</div>
                  <div
                    v-for="(rule, rIdx) in rulesFor(item)"
                    :key="item.label + '-cr-' + (rule.feedback_id || 'n') + '-' + rIdx"
                    class="coexist-row"
                  >
                    <span class="coexist-name">כלל {{ rIdx + 1 }}</span>
                    <span class="coexist-chip" :class="'chip-' + (rule.rule_status || 'none')">{{ ruleStatusLabel(rule.rule_status) }}</span>
                    <span class="coexist-text" :title="rule.rule_text">{{ rule.rule_text || '—' }}</span>
                    <button
                      v-if="rule.editable"
                      type="button"
                      class="coexist-edit"
                      :disabled="editorSubmitting"
                      @click="openEditEditor(item, rule, rIdx, $event)"
                    >ערוך</button>
                    <span v-else class="coexist-lock" :title="lockTooltip(rule.rule_status)" aria-label="נעול">🔒</span>
                  </div>

                  <button
                    type="button"
                    class="coexist-add"
                    :disabled="atCeiling(item) || (editor && editor.label !== item.label)"
                    :title="atCeiling(item) ? 'הגעת למקסימום 5 כללים בקטגוריה זו' : ''"
                    @click="openAddEditor(item, $event)"
                  >+ הוסף כלל חדש</button>
                  <span v-if="atCeiling(item)" class="coexist-helper">הגעת למקסימום 5 כללים בקטגוריה זו</span>
                </div>
              </template>

              <!-- Single-mode editor (amber = edit, blue = add), one open at a time -->
              <div
                v-if="editorIsOpenFor(item.label)"
                class="coexist-editor"
                :class="editor.mode === 'edit' ? 'ce-edit' : 'ce-add'"
              >
                <div class="coexist-editor-title">
                  {{ editor.mode === 'edit' ? ('עריכת כלל ' + editor.ruleIndex + ' · ' + editor.statusLabel) : 'כלל חדש' }}
                </div>
                <textarea
                  class="textarea coexist-editor-textarea"
                  rows="3"
                  dir="rtl"
                  v-model="editor.text"
                  :placeholder="`כתבי מה צריך לתקן בדיוק עבור ${item.label}`"
                ></textarea>
                <div class="coexist-editor-actions">
                  <button
                    type="button"
                    class="button is-small is-primary"
                    :class="{ 'is-loading': editorSubmitting }"
                    :disabled="editorSubmitting"
                    @click="submitRuleEditor(item)"
                  >{{ editor.mode === 'edit' ? 'שמרי שינויים' : 'הוסיפי כלל' }}</button>
                  <button
                    type="button"
                    class="button is-small"
                    :disabled="editorSubmitting"
                    @click="closeEditor()"
                  >בטלי</button>
                </div>
              </div>
            </div>

            <!-- Empty state / no agent_type / disclosure-error: today's plain note box -->
            <template v-if="showPlainNoteBox(item)">
              <div class="reasoning-component-toolbar">
                <span class="inline-tip">שליחה של קטגוריה זו בלבד — אפשר לשלוח קטגוריה נוספת עכשיו או לחזור מאוחר יותר. ההצעה תישאר פתוחה.</span>
              </div>
              <div class="reasoning-component-editor">
                <textarea
                  class="textarea difficulty-component-textarea"
                  rows="3"
                  v-model="componentDrafts[item.label]"
                  :placeholder="`כתבי מה צריך לתקן בדיוק עבור ${item.label}`"
                ></textarea>
              </div>
              <div v-if="sentCategoryMap[item.label]" class="category-sent">
                ✓ נשלח{{ sentCategoryMap[item.label].by ? ' · ' + sentCategoryMap[item.label].by : '' }}{{ sentCategoryMap[item.label].at ? ' · ' + formatSentAt(sentCategoryMap[item.label].at) : '' }}
              </div>
              <button
                class="button is-small is-primary send-button"
                type="button"
                :class="{ 'is-loading': is_submitting && submittingLabel === item.label }"
                :disabled="is_submitting"
                @click="sendDifficultyComponent(item)"
              >
                {{ sentCategoryMap[item.label] ? 'שלח שוב' : 'שלח' }}
              </button>
            </template>
          </section>
        </div>
        <div v-if="remainingCategories.length" class="add-category">
          <label class="add-category-label" for="add-category-select">
            רוצה להתייחס לקטגוריה שאינה מופיעה? בחרי קטגוריה נוספת:
          </label>
          <div class="add-category-controls">
            <select
              id="add-category-select"
              class="add-category-select"
              v-model="selectedCategory"
              :disabled="is_submitting"
            >
              <option value="">בחרי קטגוריה…</option>
              <option v-for="cat in remainingCategories" :key="cat" :value="cat">{{ cat }}</option>
            </select>
            <button
              type="button"
              class="button is-small is-primary"
              :disabled="!selectedCategory || is_submitting"
              @click="addCategory"
            >
              הוסף
            </button>
          </div>
        </div>
      </div>

      <div class="details-grid">
        <div class="detail-row" v-if="proposal.applicant_name">
          <span class="label">מועמד:</span>
          <span class="value">{{ proposal.applicant_name }}</span>
          <span class="value phone" v-if="proposal.applicant_phone"> · {{ proposal.applicant_phone }}</span>
        </div>
        <div class="detail-row" v-if="proposal.position_title">
          <span class="label">משרה:</span>
          <span class="value">{{ proposal.position_title }}</span>
        </div>
        <div class="detail-row" v-if="proposal.company_name">
          <span class="label">חברה:</span>
          <span class="value">{{ proposal.company_name }}</span>
        </div>
        <div class="detail-row">
          <span class="label">מס׳ הצעה:</span>
          <span class="value">{{ proposal.offer_id }}</span>
        </div>
        <div class="detail-row" v-if="proposal.recruiter_action">
          <span class="label">החלטת מגייסת:</span>
          <span class="value">{{ recruiterActionLabel(proposal.recruiter_action) }}</span>
        </div>
        <div class="detail-row" v-if="proposal.ba_decision">
          <span class="label">החלטת בטר אייג'נטס:</span>
          <span class="value">{{ baDecisionLabel(proposal.ba_decision) }}</span>
        </div>
      <div class="detail-row">
        <span class="label">סטטוס:</span>
        <span class="value">{{ proposal.status_label }}</span>
      </div>
    </div>

      <div v-if="proposal.status === 'handled'">
        <div class="reasoning-box handled" v-if="proposal.recruiter_note">
          <h4>הערת מגייסת</h4>
          <p>{{ proposal.recruiter_note }}</p>
        </div>
        <div class="reasoning-box handled" v-if="proposal.improvement_plan">
          <h4>הצעה לשיפור שנשלחה</h4>
          <p class="pre">{{ proposal.improvement_plan }}</p>
        </div>
        <p class="handled-note">הצעה זו טופלה {{ proposal.handled_by ? 'על ידי ' + proposal.handled_by : '' }}.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import LinkEntity from '@/components/LinkEntity'

// Canonical list of Better Agents difficulty dimensions (Hebrew labels), in the
// order BA/rebase_server present them (mirrors DIMENSION_LABELS_HE on the server
// and the sort order used when parsing ba_reasoning). This is the single source
// of truth for the "add another category" dropdown.
const DIFFICULTY_CATEGORIES = [
  'כישורים',
  'תפקידים',
  'ניסיון',
  'השכלה',
  'מיקום',
  'שפות',
  'שכר',
  'סקטור',
  'יציבות תעסוקתית'
]

export default {
  name: 'ChatbotProposalHandleModal',
  components: {
    LinkEntity
  },
  props: {
    payload: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      proposal: {},
      componentDrafts: {},
      manualCategories: [],
      selectedCategory: '',
      is_loading: true,
      is_submitting: false,
      submittingLabel: null,
      is_category_refreshing: false,
      recalculatingLabel: null,
      categoryVerdicts: {},
      // Coexisting learned rules, per dimension (Hebrew label). `disclosureByLabel`
      // holds { rules, count, ceiling, identity } from the server; the loading/error
      // maps drive the skeleton and the non-blocking retry line.
      disclosureByLabel: {},
      disclosureLoading: {},
      disclosureError: {},
      // Single-mode editor, ONE open at a time across ALL dimensions:
      //   { label, mode: 'add_new'|'edit', feedbackId, ruleIndex, statusLabel, text }
      editor: null,
      editorSubmitting: false,
      lastTriggerRef: null
    }
  },
  computed: {
    parsedDifficultyReasons () {
      if (!this.proposal || !this.proposal.ba_reasoning) return []
      return this.parseDifficultyReasoning(this.proposal.ba_reasoning)
    },
    // Structured per-dimension breakdown BA now returns, keyed by Hebrew label:
    // { agent_type, decision, candidate_side, position_side, why_no, why_yes }.
    // Empty for older proposals (they only have the flat ba_reasoning text).
    breakdownByLabel () {
      const map = {}
      const list = (this.proposal && this.proposal.ba_breakdown) || []
      if (Array.isArray(list)) {
        list.forEach(entry => {
          if (entry && entry.label) map[entry.label] = entry
        })
      }
      return map
    },
    // The difficulty categories to show, in canonical order:
    //   * structured breakdown labels when BA supplied them (preferred),
    //   * else the labels parsed from the flat ba_reasoning (older proposals),
    //   * plus any category the recruiter manually added.
    // Each item carries both `breakdown` (structured, may be null) and `reasons`
    // (flat fallback) so the template renders whichever is available.
    displayDifficultyReasons () {
      const breakdown = this.breakdownByLabel
      const breakdownLabels = Object.keys(breakdown)
      const parsed = this.parsedDifficultyReasons
      const reasonsByLabel = {}
      parsed.forEach(item => { reasonsByLabel[item.label] = item.reasons })

      const primaryLabels = breakdownLabels.length ? breakdownLabels : parsed.map(item => item.label)
      const listedLabels = new Set(primaryLabels)
      const orderIndex = label => {
        const i = DIFFICULTY_CATEGORIES.indexOf(label)
        return i === -1 ? DIFFICULTY_CATEGORIES.length : i
      }

      const primary = primaryLabels
        .map(label => ({
          label,
          agent_type: breakdown[label] ? breakdown[label].agent_type : null,
          decision: breakdown[label] ? breakdown[label].decision : null,
          breakdown: breakdown[label] || null,
          reasons: reasonsByLabel[label] || [],
          manual: false
        }))
        .sort((a, b) => orderIndex(a.label) - orderIndex(b.label))

      const manual = this.manualCategories
        .filter(label => !listedLabels.has(label))
        .map(label => ({ label, agent_type: null, decision: null, breakdown: null, reasons: [], manual: true }))

      return primary.concat(manual)
    },
    // Every canonical category that is not already shown (neither parsed from BA
    // nor manually added yet), so the recruiter can comment on it.
    remainingCategories () {
      const shown = new Set(this.displayDifficultyReasons.map(item => item.label))
      return DIFFICULTY_CATEGORIES.filter(label => !shown.has(label))
    },
    // Categories already submitted (per-category), keyed by Hebrew label. Hydrated
    // from the server's handled_categories so reopening the modal shows what was
    // already sent (e.g. שכר sent now, מיקום still pending).
    sentCategoryMap () {
      const map = {}
      const list = (this.proposal && this.proposal.handled_categories) || []
      if (Array.isArray(list)) {
        list.forEach(entry => {
          if (entry && entry.label) {
            map[entry.label] = entry
          }
        })
      }
      return map
    },
    // Human-readable current salary for the שכר criterion: a min–max range when
    // both exist, else whichever single value or the free-text comment. 99,999 is
    // the "no cap" sentinel, so we label it rather than print the raw number.
    salaryDisplay () {
      const salary = this.proposal && this.proposal.position_salary
      if (!salary) return '—'
      if (salary.cap_ignored) return 'ללא תקרה (מדולג — לשיקול המגייסת)'
      const parts = []
      if (salary.min_salary) parts.push(salary.min_salary)
      if (salary.max_salary) parts.push(salary.max_salary)
      if (parts.length) return parts.join('–')
      if (salary.comment) return salary.comment
      return '—'
    }
  },
  created () {
    this.fetchDetails()
  },
  methods: {
    ...mapMutations('modals', ['create', 'remove']),
    addCategory () {
      const label = (this.selectedCategory || '').trim()
      if (!label) {
        return
      }
      if (!DIFFICULTY_CATEGORIES.includes(label)) {
        return
      }
      if (!this.manualCategories.includes(label)) {
        this.manualCategories.push(label)
      }
      if (this.componentDrafts[label] === undefined) {
        this.$set(this.componentDrafts, label, '')
      }
      this.selectedCategory = ''
    },
    proposalId () {
      const data = this.payload.data || this.payload
      return data.id
    },
    // Make sure every category that was already submitted shows up as a section
    // (with its "✓ נשלח" badge) when the modal is reopened — even categories the
    // recruiter added manually and that BA never flagged.
    seedManualCategoriesFromHandled () {
      const list = (this.proposal && this.proposal.handled_categories) || []
      if (!Array.isArray(list)) {
        return
      }
      const shownLabels = new Set(this.parsedDifficultyReasons.map(item => item.label))
      Object.keys(this.breakdownByLabel).forEach(label => shownLabels.add(label))
      list.forEach(entry => {
        const label = entry && entry.label
        if (label &&
            DIFFICULTY_CATEGORIES.includes(label) &&
            !shownLabels.has(label) &&
            !this.manualCategories.includes(label)) {
          this.manualCategories.push(label)
        }
      })
    },
    fetchDetails () {
      this.is_loading = true
      this.$network_utils
        .getChatbotProposal({ id: this.proposalId() })
        .then(response => {
          this.proposal = response
          this.seedManualCategoriesFromHandled()
          this.rebuildComponentDrafts()
          this.refreshAllDisclosures()
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: 'שגיאה בטעינת פרטי ההצעה', type: 'error' })
        })
        .finally(() => {
          this.is_loading = false
        })
    },
    // Direction the disclosure/submit use, mirroring the server mapping. A row
    // with no recruiter stance degrades to complex_case (disclosure still resolves;
    // an unsupported submit surfaces the DEFAULT save-failed toast, never a block).
    disclosureDirection () {
      switch ((this.proposal && this.proposal.recruiter_action) || '') {
        case 'reject': return 'recruiter_reject_vs_ba_submit'
        case 'submit': return 'recruiter_submit_vs_ba_reject'
        default: return 'complex_case'
      }
    },
    generateRequestId () {
      // RFC4122-ish v4; no crypto dependency required for an idempotency key.
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0
        const v = c === 'x' ? r : (r & 0x3 | 0x8)
        return v.toString(16)
      })
    },
    // Fetch coexisting rules for every dimension that has an agent_type (identity is
    // keyed on it). Dimensions without one keep the plain note box only.
    refreshAllDisclosures () {
      this.displayDifficultyReasons
        .filter(item => item.agent_type)
        .forEach(item => this.fetchDisclosure(item))
    },
    fetchDisclosure (item) {
      const label = item.label
      this.$set(this.disclosureLoading, label, true)
      this.$set(this.disclosureError, label, false)
      this.$network_utils
        .getDisclosure({
          id: this.proposalId(),
          direction: this.disclosureDirection(),
          implicated_agent_type: item.agent_type
        })
        .then(response => {
          this.$set(this.disclosureByLabel, label, {
            rules: (response && Array.isArray(response.rules)) ? response.rules : [],
            count: (response && typeof response.count === 'number') ? response.count : 0,
            ceiling: (response && typeof response.ceiling === 'number') ? response.ceiling : 5,
            identity: response && response.identity
          })
        })
        .catch(() => {
          // Non-blocking: mark the error, degrade to plain Add-new (never block).
          this.$set(this.disclosureError, label, true)
        })
        .finally(() => {
          this.$set(this.disclosureLoading, label, false)
        })
    },
    rulesFor (item) {
      const d = this.disclosureByLabel[item.label]
      return d && Array.isArray(d.rules) ? d.rules : []
    },
    ceilingFor (item) {
      const d = this.disclosureByLabel[item.label]
      return d && typeof d.ceiling === 'number' ? d.ceiling : 5
    },
    countFor (item) {
      const d = this.disclosureByLabel[item.label]
      return d && typeof d.count === 'number' ? d.count : this.rulesFor(item).length
    },
    // A dimension shows the coexisting-rules card only when disclosure loaded rules.
    hasCoexistingRules (item) {
      return !!item.agent_type && !this.disclosureLoading[item.label] &&
        !this.disclosureError[item.label] && this.rulesFor(item).length > 0
    },
    // Empty state (0 rules, loaded, no error) OR a dimension without an agent_type:
    // fall back to today's plain note box. Disclosure error also degrades here.
    showPlainNoteBox (item) {
      if (this.disclosureLoading[item.label]) return false
      return !this.hasCoexistingRules(item)
    },
    atCeiling (item) {
      return this.countFor(item) >= this.ceilingFor(item)
    },
    ruleStatusLabel (status) {
      switch (status) {
        case 'draft': return 'טיוטה'
        case 'approved': return 'מאושר'
        case 'rejected': return 'נדחה'
        case 'superseded': return 'הוחלף'
        default: return 'ממתין'
      }
    },
    lockTooltip (status) {
      return status === 'approved'
        ? 'הכלל אושר וננעל — הוסיפי כלל חדש שיחול לצידו'
        : 'הכלל הוחלף בכלל חדש יותר — הוסיפי כלל חדש'
    },
    editorIsOpenFor (label) {
      return !!this.editor && this.editor.label === label
    },
    // Opening any editor closes any other; a dirty open editor asks first.
    confirmDiscardIfDirty () {
      if (this.editor && (this.editor.text || '').trim().length > 0) {
        return window.confirm('יש טיוטה פתוחה שלא נשמרה. לפתוח עריכה אחרת ולוותר עליה?')
      }
      return true
    },
    openAddEditor (item, ev) {
      if (this.atCeiling(item)) return
      if (!this.confirmDiscardIfDirty()) return
      this.lastTriggerRef = ev && ev.currentTarget ? ev.currentTarget : null
      // Mint a FRESH idempotency key each time an Add-new editor is opened, and
      // store it on the editor's state. It is reused for retries of THIS editor
      // (so a double-click / resubmit after a transient error replays idempotently
      // rather than duplicating), and discarded when the add succeeds or the editor
      // is closed — so the NEXT distinct Add-new on the same dimension mints a NEW
      // request_id and can never collide with the previous one (Bug #4).
      this.editor = { label: item.label, agent_type: item.agent_type, mode: 'add_new', feedbackId: null, ruleIndex: null, statusLabel: '', text: '', requestId: this.generateRequestId() }
    },
    openEditEditor (item, rule, ruleIndex, ev) {
      if (!rule.editable) return
      if (!this.confirmDiscardIfDirty()) return
      this.lastTriggerRef = ev && ev.currentTarget ? ev.currentTarget : null
      this.editor = {
        label: item.label,
        agent_type: item.agent_type,
        mode: 'edit',
        feedbackId: rule.feedback_id,
        ruleIndex: ruleIndex + 1,
        statusLabel: this.ruleStatusLabel(rule.rule_status),
        text: rule.rule_text || '',
        // Edit is keyed by feedback_id, not request_id — no idempotency key needed.
        requestId: null
      }
    },
    closeEditor () {
      this.editor = null
      const trigger = this.lastTriggerRef
      this.lastTriggerRef = null
      if (trigger && typeof trigger.focus === 'function') {
        this.$nextTick(() => { try { trigger.focus() } catch (e) { /* focus best-effort */ } })
      }
    },
    // Classify a single per-item result into a machine code the UI acts on. Success
    // is ONLY a truthful ok; everything else is a distinct non-mutating warning.
    classifyResult (result) {
      if (!result || typeof result !== 'object') return 'default'
      if (result.ok === true) {
        if (result.mode === 'edit') return 'edit_ok'
        if (Number(result.status) === 200) return 'replay_ok'
        return 'add_ok'
      }
      const known = ['rule_approved', 'rule_superseded', 'rule_limit', 'unknown_handle']
      if (known.indexOf(result.code) !== -1) return result.code
      return 'default'
    },
    // Map code -> toast. Mutate/refetch the list ONLY on a real ok. Returns the code.
    handleFeedbackResult (item, result) {
      const code = this.classifyResult(result)
      switch (code) {
        case 'add_ok':
          this.$utils.emit('show_toast', { text: 'כלל חדש נוסף ונשלח ללמידה.' })
          this.closeEditor()
          this.fetchDisclosure(item)
          break
        case 'edit_ok':
          this.$utils.emit('show_toast', { text: 'הכלל עודכן ונשלח ללמידה.' })
          this.closeEditor()
          this.fetchDisclosure(item)
          break
        case 'replay_ok':
          this.$utils.emit('show_toast', { text: 'הכלל כבר נשמר קודם — לא נוצר כפל.' })
          this.closeEditor()
          this.fetchDisclosure(item)
          break
        case 'rule_approved':
          this.$utils.emit('show_toast', { text: 'אי אפשר לערוך: הכלל כבר אושר וננעל. הוסיפי כלל חדש שיחול לצידו.', type: 'error' })
          break
        case 'rule_superseded':
          this.$utils.emit('show_toast', { text: 'לא נשמר כלום: הכלל שניסית לערוך הוחלף בכלל חדש יותר. פתחי את הכלל העדכני או הוסיפי כלל חדש.', type: 'error' })
          break
        case 'rule_limit':
          this.$utils.emit('show_toast', { text: 'לא נוסף כלל: הגעת לתקרה של 5 כללים לקריטריון זה. מחקי או ערכי כלל קיים ונסי שוב.', type: 'error' })
          break
        case 'unknown_handle':
          this.$utils.emit('show_toast', { text: 'הכלל שניסית לערוך כבר לא קיים — רועננתי את הרשימה. בדקי מה קיים והוסיפי כלל חדש אם צריך.', type: 'error' })
          // Refetch so the list reflects reality; keep the editor input for the user.
          this.fetchDisclosure(item)
          break
        default:
          // Unrecognized code / missing code / thrown network-or-TIMEOUT error: never
          // mutate optimistically, keep the editor + its input so the user can retry.
          this.$utils.emit('show_toast', { text: 'השמירה נכשלה. לא הצלחנו לאמת שהכלל נשמר, ולכן לא שינינו את הרשימה. נסי שוב.', type: 'error' })
      }
      return code
    },
    // Submit the open editor (Add-new or Edit) through the coexisting-rules relay.
    submitRuleEditor (item) {
      if (this.editorSubmitting) return
      const editor = this.editor
      if (!editor || editor.label !== item.label) return
      const text = (editor.text || '').trim()
      if (!text) {
        this.$utils.emit('show_toast', { text: 'כתבי סיבה קצרה לפני השליחה', type: 'error' })
        return
      }
      this.editorSubmitting = true
      this.$network_utils
        .submitDisagreementFeedback({
          id: this.proposalId(),
          direction: this.disclosureDirection(),
          // Per-editor idempotency key (add_new only). null for edit (feedback_id
          // is the handle). A transient failure keeps the editor open → the same
          // request_id is retried; success/close discards it → the next Add-new
          // opens with a new one, so two distinct Add-news never collide (Bug #4).
          request_id: editor.requestId,
          items: [{
            implicated_agent_type: editor.agent_type,
            explanation: text,
            mode: editor.mode,
            feedback_id: editor.feedbackId
          }]
        })
        .then(response => {
          const result = response && Array.isArray(response.results) ? response.results[0] : null
          this.handleFeedbackResult(item, result)
        })
        .catch(error => {
          // A 409/404 rejects with the coded results in the body; a timeout/network
          // error has no results -> classifyResult returns 'default' (save-failed).
          const data = error && error.response && error.response.data
          const result = data && Array.isArray(data.results) ? data.results[0] : null
          this.handleFeedbackResult(item, result)
        })
        .finally(() => {
          this.editorSubmitting = false
        })
    },
    // Submit the improvement note for ONE difficulty category. Each category is
    // sent independently and the modal stays open: the recruiter can send שכר now
    // and מיקום in ten minutes. The proposal is NOT auto-closed here — its overall
    // status is toggled manually from the list when the recruiter is done. The note
    // now feeds the better_agents rules-learning pipeline (server relays it); the
    // toast keys off `learning_relay` for the exact wording.
    sendDifficultyComponent (item) {
      if (this.is_submitting) {
        return
      }
      const note = this.composeDifficultySectionText(item)
      this.is_submitting = true
      this.submittingLabel = item.label
      this.$network_utils
        .handleChatbotProposal({
          id: this.proposalId(),
          recruiter_note: note,
          category: item.label
        })
        .then(response => {
          const sentMsg = response && response.learning_relay === 'ok'
            ? 'ההערה נשלחה ללמידת המערכת'
            : 'ההערה נשמרה (הלמידה תנוסה שוב מאוחר יותר)'
          this.$utils.emit('show_toast', { text: sentMsg })
          // Reflect the server's per-category state so the "✓ נשלח" badge appears
          // and survives a reopen. We deliberately keep the modal open.
          if (response && Array.isArray(response.handled_categories)) {
            this.$set(this.proposal, 'handled_categories', response.handled_categories)
          }
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: `שגיאה בשליחת הקטגוריה "${item.label}"`, type: 'error' })
        })
        .finally(() => {
          this.is_submitting = false
          this.submittingLabel = null
        })
    },
    formatSentAt (value) {
      if (!value) {
        return ''
      }
      const d = new Date(value)
      if (isNaN(d.getTime())) {
        return ''
      }
      return d.toLocaleDateString('he-IL') + ' ' + d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
    },
    hasBreakdown (breakdown) {
      return !!(breakdown && (breakdown.candidate_side || breakdown.position_side ||
        breakdown.why_no || breakdown.why_yes))
    },
    composeDifficultySectionText (item) {
      const lines = [`רכיב: ${item.label}`]
      if (this.hasBreakdown(item.breakdown)) {
        if (item.breakdown.candidate_side) lines.push(`מה יש למועמד/ת: ${item.breakdown.candidate_side}`)
        if (item.breakdown.position_side) lines.push(`מה דורשת המשרה: ${item.breakdown.position_side}`)
        if (item.breakdown.why_no) lines.push(`למה לא: ${item.breakdown.why_no}`)
        if (item.breakdown.why_yes) lines.push(`למה כן: ${item.breakdown.why_yes}`)
      } else {
        (item.reasons || []).forEach(reason => {
          lines.push(`- ${reason}`)
        })
      }

      const draft = (this.componentDrafts[item.label] || '').trim()
      if (draft) {
        lines.push(`הערת תיקון: ${draft}`)
      } else {
        lines.push('הערת תיקון:')
      }

      return lines.join('\n')
    },
    rebuildComponentDrafts () {
      const next = {}
      this.displayDifficultyReasons.forEach(item => {
        next[item.label] = this.componentDrafts[item.label] || ''
      })
      this.componentDrafts = next
    },
    // Re-check ONLY this category via the dedicated per-category API: synchronous,
    // works from any offer status, operation-aware. Shows the fresh verdict inline
    // without touching the stored proposal.
    refreshDifficultyComponent (item) {
      if (this.is_category_refreshing) {
        return
      }
      const label = item && item.label ? item.label : ''
      if (!label) {
        return
      }
      this.recalculatingLabel = label
      this.is_category_refreshing = true
      this.$network_utils
        .recalculateChatbotProposalCategory({ id: this.proposalId(), label: label })
        .then(response => {
          this.$set(this.categoryVerdicts, label, {
            decision: response && response.decision,
            reasoning: (response && response.reasoning) || ''
          })
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: `שגיאה בבדיקה מחדש של ${label}`, type: 'error' })
        })
        .finally(() => {
          this.is_category_refreshing = false
          this.recalculatingLabel = null
        })
    },
    verdictLabel (decision) {
      switch (decision) {
        case 'green': return 'נבדק מחדש — כעת מתאים (תוקן) ✓'
        case 'orange': return 'נבדק מחדש — עדיין חלקי (כתום)'
        case 'red': return 'נבדק מחדש — עדיין קושי (אדום)'
        default: return 'נבדק מחדש'
      }
    },
    close () {
      this.remove(this.payload)
    },
    recruiterActionLabel (action) {
      return action === 'reject' ? 'פסילה' : action === 'submit' ? 'הגשה' : action
    },
    baDecisionLabel (decision) {
      switch (decision) {
        case 'submit': return 'הגשה'
        case 'reject': return 'פסילה'
        case 'needs_info': return 'נדרש מידע נוסף'
        default: return decision
      }
    },
    parseDifficultyReasoning (text) {
      const lines = String(text)
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)

      if (!lines.length) {
        return []
      }

      const order = ['כישורים', 'תפקידים', 'ניסיון', 'השכלה', 'מיקום', 'שפות', 'שכר', 'סקטור', 'יציבות תעסוקתית']
      const knownIndex = Object.fromEntries(order.map((label, index) => [label, index]))
      const grouped = {}

      for (const line of lines) {
        const match = line.match(/^([^:]+)\s*:\s*(.+)$/)
        if (!match) {
          continue
        }

        const label = match[1].trim()
        const value = match[2].trim()
        if (!label || !value) {
          continue
        }

        if (!grouped[label]) grouped[label] = []
        grouped[label].push(value)
      }

      const hasEntries = Object.keys(grouped).length > 0
      if (!hasEntries) {
        return []
      }

      const recognized = Object.keys(grouped)
        .map((label) => ({
          label,
          reasons: grouped[label]
        }))
        .sort((a, b) => {
          const left = knownIndex[a.label] != null ? knownIndex[a.label] : Number.MAX_SAFE_INTEGER
          const right = knownIndex[b.label] != null ? knownIndex[b.label] : Number.MAX_SAFE_INTEGER
          if (left !== right) return left - right
          return a.label.localeCompare(b.label)
        })

      return recognized
    }
  }
}
</script>

<style scoped>
.chatbot-proposal-modal {
  padding: 8px 4px;
  text-align: right;
  min-width: 520px;
}
.loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 0;
}
.details-grid {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
}
.detail-row {
  margin-bottom: 6px;
  font-size: 14px;
}
.detail-row .label {
  font-weight: 700;
  color: #2c3e50;
  margin-left: 6px;
}
.detail-row .phone {
  color: #7f8c8d;
}
.reasoning-box {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 14px;
  background: #fff;
}
.reasoning-box.handled {
  background: #f0f7ff;
  border-color: #cfe3ff;
}
.reasoning-box h4 {
  margin: 0 0 6px 0;
  font-size: 14px;
  font-weight: 700;
  color: #1565c0;
}
.reasoning-box p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #34495e;
}
.reasoning-components {
  margin-top: 6px;
}
.reasoning-component {
  border: 1px solid #dbeafe;
  background: #f8fbff;
  border-radius: 10px;
  padding: 10px 12px;
  margin-top: 10px;
}
.reasoning-component:first-child {
  margin-top: 0;
}
.reasoning-component-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.refresh-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid #cfe0f5;
  border-radius: 6px;
  background: #fff;
  color: #1565c0;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
}
.refresh-icon-button:hover:not(:disabled) {
  background: #eef5ff;
  border-color: #1565c0;
}
.refresh-icon-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.refresh-icon-button.is-spinning svg {
  animation: rc-refresh-spin 0.8s linear infinite;
}
@keyframes rc-refresh-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.category-verdict {
  margin-top: 8px;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}
.category-verdict.verdict-green {
  background: #eafaf1;
  border: 1px solid #a7e9c5;
}
.category-verdict.verdict-orange {
  background: #fff6e9;
  border: 1px solid #ffd9a0;
}
.category-verdict.verdict-red {
  background: #fdecea;
  border: 1px solid #f5b7b1;
}
.verdict-badge {
  display: block;
  font-weight: 700;
  margin-bottom: 4px;
}
.verdict-green .verdict-badge { color: #1e7a46; }
.verdict-orange .verdict-badge { color: #b9770e; }
.verdict-red .verdict-badge { color: #c0392b; }
.verdict-reasoning {
  margin: 0;
  line-height: 1.5;
  color: #34495e;
  white-space: pre-wrap;
}
.reasoning-components-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 4px;
}
.reasoning-component-toolbar {
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.send-button {
  margin-top: 8px;
}
.category-sent {
  margin-top: 8px;
  display: inline-block;
  background: #eafaf1;
  border: 1px solid #a7e9c5;
  color: #1e7a46;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
}
.inline-tip {
  color: #4b5563;
  font-size: 12px;
}
.reasoning-component-editor {
  margin-top: 8px;
}
.difficulty-component-textarea {
  min-height: 82px;
  font-size: 13px;
  line-height: 1.35;
  width: 100%;
}
.reasoning-component h5 {
  margin: 0 0 4px 0;
  font-size: 13px;
  font-weight: 700;
  color: #1d3557;
}
.breakdown-grid {
  margin: 4px 0 2px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.bd-row {
  display: block;
  font-size: 13px;
  line-height: 1.5;
  color: #34495e;
}
.bd-label {
  font-weight: 700;
  color: #1d3557;
  margin-left: 6px;
}
.bd-value {
  color: #34495e;
  white-space: pre-wrap;
}
.bd-row.bd-no {
  background: #fdecea;
  border-radius: 6px;
  padding: 6px 8px;
}
.bd-row.bd-no .bd-label {
  color: #c0392b;
}
.bd-row.bd-yes {
  background: #eafaf1;
  border-radius: 6px;
  padding: 6px 8px;
}
.bd-row.bd-yes .bd-label {
  color: #1e7a46;
}
.salary-edit {
  margin-top: 8px;
  padding: 8px 10px;
  background: #f4f8ff;
  border: 1px solid #d6e4ff;
  border-radius: 8px;
}
.salary-values {
  font-size: 13px;
  margin-bottom: 4px;
}
.salary-values-label {
  font-weight: 700;
  color: #1d3557;
  margin-left: 6px;
}
.salary-values-text {
  color: #34495e;
}
.salary-link {
  margin-bottom: 4px;
}
.salary-hint {
  margin: 0;
  font-size: 12px;
  color: #4b5563;
}
.reasoning-list {
  margin: 0 0 2px 20px;
  padding: 0;
}
.reasoning-list li {
  margin-bottom: 2px;
  line-height: 1.5;
  color: #34495e;
}
.reasoning-box p.pre {
  white-space: pre-wrap;
}
.raw-reasoning {
  white-space: pre-wrap;
}
.reasoning-component.is-manual {
  border-style: dashed;
  border-color: #b9c6da;
  background: #fbfdff;
}
.manual-hint {
  margin: 0 0 2px 0;
  font-size: 12px;
  color: #6b7280;
}
.add-category {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #d7e1ee;
}
.add-category-label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
}
.add-category-controls {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}
.add-category-select {
  flex: 1 1 auto;
  min-width: 180px;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  border: 1px solid #cbd5e0;
  background: #fff;
}
.handle-section {
  margin-top: 8px;
}
.note-label {
  display: block;
  font-weight: 700;
  margin-bottom: 8px;
  color: #2c3e50;
}
.checkbox-line {
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #2c3e50;
  cursor: pointer;
  font-size: 14px;
}
.checkbox-line input {
  margin: 0;
}
.textarea {
  width: 100%;
  border-radius: 8px;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #cbd5e0;
  resize: vertical;
}
.actions {
  display: flex;
  gap: 12px;
  margin-top: 14px;
}
.operator-section {
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid #edf2f7;
}
.section-label {
  font-size: 13px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
}
.operator-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.handled-note {
  color: #7f8c8d;
  font-size: 13px;
  margin-top: 8px;
}

/* ── Coexisting learned rules (per dimension) ─────────────────────────────── */
.coexist {
  margin-top: 10px;
}
.coexist-skeleton {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 56px;
}
.coexist-skeleton .sk-line {
  height: 14px;
  width: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #eef1f5 25%, #e2e7ee 37%, #eef1f5 63%);
  background-size: 400% 100%;
  animation: coexist-shimmer 1.2s ease-in-out infinite;
}
.coexist-skeleton .sk-short {
  width: 55%;
}
@keyframes coexist-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}
.coexist-error {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #c0392b;
  background: #fdecea;
  border: 1px solid #f5b7b1;
  border-radius: 8px;
  padding: 6px 10px;
}
.link-like {
  background: none;
  border: none;
  color: #1565c0;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  text-decoration: underline;
}
.coexist-card {
  background: #f8fbff;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  padding: 10px 12px;
}
.coexist-head {
  font-size: 12px;
  font-weight: 700;
  color: #1d3557;
  margin-bottom: 6px;
}
.coexist-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-top: 1px solid #eaf1fb;
}
.coexist-row:first-of-type {
  border-top: none;
}
.coexist-name {
  font-size: 12px;
  font-weight: 700;
  color: #1d3557;
  flex-shrink: 0;
}
.coexist-chip {
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  padding: 2px 8px;
  flex-shrink: 0;
}
.chip-draft { background: #fff6e9; color: #b9770e; border: 1px solid #ffd9a0; }
.chip-approved { background: #eafaf1; color: #1e7a46; border: 1px solid #a7e9c5; }
.chip-rejected { background: #fdecea; color: #c0392b; border: 1px solid #f5b7b1; }
.chip-superseded { background: #f1f3f5; color: #6b7280; border: 1px solid #dfe3e8; }
.chip-none { background: #eef2f7; color: #4b5563; border: 1px solid #d7e1ee; }
.coexist-text {
  flex: 1 1 auto;
  font-size: 12px;
  color: #34495e;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.coexist-edit {
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #cfe0f5;
  color: #1565c0;
  border-radius: 6px;
  font-size: 12px;
  padding: 2px 10px;
  cursor: pointer;
}
.coexist-edit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.coexist-lock {
  flex-shrink: 0;
  font-size: 13px;
  opacity: 0.7;
}
.coexist-add {
  margin-top: 8px;
  width: 100%;
  border: 1px dashed #9db6d8;
  background: #fbfdff;
  color: #1565c0;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.coexist-add:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.coexist-helper {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #6b7280;
}
.coexist-editor {
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  /* RTL: logical inline-start renders on the RIGHT — do NOT hardcode left. */
  border-inline-start: 3px solid #e6920e;
  background: #fff;
}
.coexist-editor.ce-add {
  border-inline-start-color: #1565c0;
  background: #f8fbff;
}
.coexist-editor.ce-edit {
  border-inline-start-color: #e6920e;
  background: #fffaf1;
}
.coexist-editor-title {
  font-size: 12px;
  font-weight: 700;
  color: #1d3557;
  margin-bottom: 6px;
}
.coexist-editor-textarea {
  min-height: 76px;
  font-size: 13px;
  line-height: 1.35;
  width: 100%;
}
.coexist-editor-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
</style>
